import { useState } from 'react';
import {
  useShadowComparisons,
  useShadowStats,
  useReviewShadow,
  type ShadowComparison,
} from '../hooks/useHatMail';

// ── Helpers ────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function matchBadge(quality: string | null) {
  switch (quality) {
    case 'exact':
      return (
        <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
          Match
        </span>
      );
    case 'close':
      return (
        <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
          Close
        </span>
      );
    case 'different':
      return (
        <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
          Different
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
          Pending
        </span>
      );
  }
}

function categoryLabel(cat: string): string {
  return cat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function originalEmailBody(comparison: ShadowComparison): string {
  return comparison.body_full || comparison.body_preview || 'No original email body available';
}

// ── Stats Summary ──────────────────────────────────────────

function StatsSummary() {
  const { data: stats, isLoading } = useShadowStats();

  if (isLoading || !stats) {
    return (
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-center justify-center py-4">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-ghw-gold" />
        </div>
      </div>
    );
  }

  const maxCatTotal = Math.max(
    ...Object.values(stats.category_accuracy).map((c) => c.total),
    1,
  );

  return (
    <div className="mb-6 space-y-4">
      {/* Header */}
      <div className="rounded-xl border border-ghw-gold/30 bg-ghw-cream/30 p-4">
        <h2 className="text-sm font-bold text-ghw-brown uppercase tracking-wide">
          Shadow Mode
        </h2>
        <p className="mt-0.5 text-xs text-gray-500">
          Side-by-side: what HatMail would say vs. what the reviewer actually sent
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Compared</p>
          <span className="mt-1 block text-2xl font-bold text-gray-900">
            {stats.total_compared}
          </span>
          <p className="mt-0.5 text-xs text-gray-400">{stats.total_reviewed} reviewed</p>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Classification
          </p>
          <span
            className={`mt-1 block text-2xl font-bold ${
              stats.accuracy_rate >= 80 ? 'text-emerald-600' : stats.accuracy_rate >= 60 ? 'text-amber-600' : 'text-red-600'
            }`}
          >
            {stats.accuracy_rate}%
          </span>
          <p className="mt-0.5 text-xs text-gray-400">accuracy rate</p>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Rubber Stamp</p>
          <span
            className={`mt-1 block text-2xl font-bold ${
              stats.rubber_stamp_rate >= 70 ? 'text-emerald-600' : stats.rubber_stamp_rate >= 50 ? 'text-amber-600' : 'text-red-600'
            }`}
          >
            {stats.rubber_stamp_rate}%
          </span>
          <p className="mt-0.5 text-xs text-gray-400">close enough to send</p>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Similarity</p>
          <span className="mt-1 block text-2xl font-bold text-gray-900">
            {stats.avg_similarity}%
          </span>
          <p className="mt-0.5 text-xs text-gray-400">avg draft match</p>
        </div>
      </div>

      {/* Category Accuracy */}
      {Object.keys(stats.category_accuracy).length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-900">Accuracy by Category</h3>
          <div className="space-y-2">
            {Object.entries(stats.category_accuracy).map(([cat, data]) => (
              <div key={cat} className="flex items-center gap-3">
                <span className="w-28 truncate text-xs text-gray-600">
                  {categoryLabel(cat)}
                </span>
                <div className="flex-1">
                  <div className="h-5 overflow-hidden rounded-full bg-gray-100">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${
                        data.rate >= 80
                          ? 'bg-emerald-400'
                          : data.rate >= 60
                            ? 'bg-amber-400'
                            : 'bg-red-400'
                      }`}
                      style={{ width: `${(data.total / maxCatTotal) * 100}%` }}
                    />
                  </div>
                </div>
                <span className="w-16 text-right text-xs font-medium text-gray-700">
                  {data.rate}% ({data.correct}/{data.total})
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Comparison Card ────────────────────────────────────────

function ComparisonCard({
  comparison,
  onReview,
  isReviewing,
}: {
  comparison: ShadowComparison;
  onReview: (id: string, correct: boolean, quality: 'exact' | 'close' | 'different') => void;
  isReviewing: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const isReviewed = comparison.reviewed_at !== null;

  return (
    <div
      className={`rounded-xl border bg-white transition-shadow ${
        isReviewed ? 'border-gray-200' : 'border-ghw-gold/40 shadow-sm'
      }`}
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-start gap-3 p-4 text-left"
        style={{ minHeight: '56px' }}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900 truncate max-w-[200px] sm:max-w-none">
              {comparison.from_name || comparison.from_email}
            </span>
            <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
              {categoryLabel(comparison.category)}
            </span>
            {matchBadge(comparison.match_quality ?? comparison.draft_quality)}
          </div>
          <p className="mt-0.5 text-sm text-gray-600 truncate">
            {comparison.subject || '(no subject)'}
          </p>
          <p className="mt-0.5 text-xs text-gray-400">{timeAgo(comparison.received_at)}</p>
        </div>
        <span className="shrink-0 text-gray-400 text-lg mt-1">{expanded ? '\u2212' : '+'}</span>
      </button>

      {/* Expanded: side-by-side */}
      {expanded && (
        <div className="border-t border-gray-100 p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* AI Draft */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-ghw-gold" />
                <span className="text-xs font-bold text-ghw-brown uppercase tracking-wide">
                  AI Draft
                </span>
              </div>
              <div className="rounded-lg border border-ghw-gold/20 bg-ghw-cream/20 p-3">
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                  {comparison.ai_draft}
                </p>
              </div>
            </div>

            {/* Human Response */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-blue-500" />
                <span className="text-xs font-bold text-blue-700 uppercase tracking-wide">
                  Actual Reply
                </span>
              </div>
              <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3">
                {comparison.human_response ? (
                  <p className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">
                    {comparison.human_response}
                  </p>
                ) : (
                  <p className="text-sm italic text-gray-400">
                    No reply captured yet
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50">
            <button
              onClick={() => setContextOpen(!contextOpen)}
              className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left"
              style={{ minHeight: '44px' }}
            >
              <span className="text-xs font-bold uppercase tracking-wide text-gray-700">
                View Original Email Context
              </span>
              <span className="text-base text-gray-400">{contextOpen ? '\u2212' : '+'}</span>
            </button>
            {contextOpen && (
              <div className="border-t border-gray-200 p-3">
                <div className="max-h-72 overflow-y-auto rounded-md bg-white p-3">
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                    {originalEmailBody(comparison)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Review Actions */}
          {!isReviewed ? (
            <div className="mt-4 border-t border-gray-100 pt-4">
              <p className="mb-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                Review this comparison
              </p>
              <div className="flex flex-wrap gap-2">
                {/* Classification review */}
                <div className="flex gap-2">
                  <button
                    onClick={() => onReview(comparison.id, true, 'exact')}
                    disabled={isReviewing}
                    className="rounded-lg bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-100 active:scale-95 disabled:opacity-50"
                    style={{ minHeight: '44px' }}
                  >
                    Correct + Match
                  </button>
                  <button
                    onClick={() => onReview(comparison.id, true, 'close')}
                    disabled={isReviewing}
                    className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100 active:scale-95 disabled:opacity-50"
                    style={{ minHeight: '44px' }}
                  >
                    Correct + Close
                  </button>
                  <button
                    onClick={() => onReview(comparison.id, true, 'different')}
                    disabled={isReviewing}
                    className="rounded-lg bg-orange-50 px-3 py-2 text-xs font-medium text-orange-700 transition-colors hover:bg-orange-100 active:scale-95 disabled:opacity-50"
                    style={{ minHeight: '44px' }}
                  >
                    Correct + Different
                  </button>
                  <button
                    onClick={() => onReview(comparison.id, false, 'different')}
                    disabled={isReviewing}
                    className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700 transition-colors hover:bg-red-100 active:scale-95 disabled:opacity-50"
                    style={{ minHeight: '44px' }}
                  >
                    Wrong Category
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4 border-t border-gray-100 pt-3">
              <p className="text-xs text-gray-400">
                Reviewed by {comparison.reviewed_by} {comparison.reviewed_at ? timeAgo(comparison.reviewed_at) : ''}
                {' | '}
                {comparison.classification_correct ? 'Classification correct' : 'Classification incorrect'}
                {comparison.draft_quality && `, draft: ${comparison.draft_quality}`}
              </p>
              {comparison.review_notes && (
                <p className="mt-1 text-xs italic text-gray-500">{comparison.review_notes}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main ShadowView ────────────────────────────────────────

export default function ShadowView() {
  const { data, isLoading, error } = useShadowComparisons();
  const review = useReviewShadow();

  const handleReview = (id: string, correct: boolean, quality: 'exact' | 'close' | 'different') => {
    review.mutate({
      id,
      classification_correct: correct,
      draft_quality: quality,
    });
  };

  return (
    <div>
      <StatsSummary />

      {/* Comparison list */}
      {isLoading && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-ghw-gold" />
        </div>
      )}

      {error && (
        <div className="rounded-xl bg-red-50 p-6 text-center">
          <p className="text-sm font-medium text-red-700">Could not load shadow comparisons</p>
          <p className="mt-1 text-xs text-red-500">{(error as Error).message}</p>
        </div>
      )}

      {data && data.comparisons.length === 0 && (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
          <p className="text-sm font-medium text-gray-600">No comparisons yet</p>
          <p className="mt-1 text-xs text-gray-400">
            Shadow comparisons will appear here once the AI pipeline processes emails and the reviewer
            sends replies from Gmail.
          </p>
        </div>
      )}

      {data && data.comparisons.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-500">
              {data.total} comparison{data.total !== 1 ? 's' : ''}
            </p>
          </div>
          {data.comparisons.map((comp) => (
            <ComparisonCard
              key={comp.id}
              comparison={comp}
              onReview={handleReview}
              isReviewing={review.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}
